import { OpenInNew } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link,
} from "@mui/material";
import { SubmissionSummary } from "@rubi/api-generated";

import { formatDateShort } from "@/common/utils";

interface LinkingDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedReport: SubmissionSummary | null;
  onConfirm: (report: SubmissionSummary) => Promise<void>;
  appName: string;
  orgId: string;
}

export const LinkingDialog = ({
  isOpen,
  onClose,
  selectedReport,
  onConfirm,
  appName,
  orgId,
}: LinkingDialogProps) => {
  return (
    <Dialog open={isOpen} onClose={onClose}>
      <DialogTitle style={{ fontWeight: "bold" }}>Confirm Link</DialogTitle>
      <DialogContent>
        {selectedReport && (
          <>
            <DialogContentText>
              Please confirm if you want to link this supplementary report to:
            </DialogContentText>
            <br />
            <DialogContentText>
              <b>
                {selectedReport.survey_name} {" - "}
                {selectedReport.tracking_number}
                <br />
              </b>
            </DialogContentText>

            <DialogContentText>
              Submitted by{" "}
              <b>
                {selectedReport.first_name} {selectedReport.last_name}
              </b>{" "}
              on {formatDateShort(selectedReport.time_created)}
            </DialogContentText>
            <Link
              href={`/${appName}/${orgId}/report/${selectedReport.id}/condensed`}
              target="_blank"
              rel="noopener"
              underline="always"
              variant="body1"
              color="primary"
              display="flex"
              alignItems="center"
              sx={{ mt: 2 }}
            >
              Click here to open report {selectedReport.tracking_number} in a
              new tab
              <OpenInNew sx={{ ml: 0.5 }} />
            </Link>
            {selectedReport.status === "modification_requested" && (
              <DialogContentText>
                <br />
                ⚠️ Important Notice: Linking this supplementary report will
                disable any previously sent Information Request links for report{" "}
                {selectedReport.tracking_number}. A notification email will also
                be sent to the reporting party. If further follow-up information
                is needed, you will need to send a new Information Request.
              </DialogContentText>
            )}
            {["approved", "rejected"].includes(selectedReport.status) && (
              <DialogContentText>
                <br />
                ⚠️ Important Notice: Linking this supplementary report will
                re-open the previously closed report{" "}
                {selectedReport.tracking_number}. A notification email will be
                sent to the reporting party. You will need to re-process report{" "}
                {selectedReport.tracking_number}.
              </DialogContentText>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions
        style={{
          display: "flex",
          justifyContent: "space-between",
          padding: "16px",
        }}
      >
        <Button onClick={onClose} color="secondary" variant="outlined">
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (!selectedReport) return;
            onConfirm(selectedReport);
          }}
          color="primary"
          variant="contained"
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};
